/**
 * Chat Image Compression Utility
 * Compresses images for chat while maintaining good visual quality
 * Target: 1200x1200 @ 85% WebP quality = ~100-300KB per image
 */

export interface CompressedImage {
  dataUrl: string;
  sizeKB: number;
  width: number;
  height: number;
}

/**
 * Compress image for chat messaging
 * Balanced compression for good quality and reasonable file size
 */
export function compressChatImage(
  dataUrl: string,
  maxWidth: number = 1200,
  maxHeight: number = 1200,
  quality: number = 0.85
): Promise<CompressedImage> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      try {
        // Calculate new dimensions maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth || height > maxHeight) {
          const widthRatio = maxWidth / width;
          const heightRatio = maxHeight / height;
          const ratio = Math.min(widthRatio, heightRatio);
          
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        
        // Create canvas and resize image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        // High-quality image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw resized image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to WebP with aggressive compression
        const compressedDataUrl = canvas.toDataURL('image/webp', quality);
        
        // Calculate size in KB
        const base64Length = compressedDataUrl.split(',')[1].length;
        const sizeKB = Math.round((base64Length * 3) / 4 / 1024);
        
        console.log(`📸 [ChatImage] Compressed: ${img.width}x${img.height} → ${width}x${height}, ${sizeKB}KB`);
        
        resolve({
          dataUrl: compressedDataUrl,
          sizeKB,
          width,
          height
        });
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = dataUrl;
  });
}

/**
 * Convert base64 data URL to Blob for upload
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const arr = dataUrl.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/webp';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  return new Blob([u8arr], { type: mime });
}

/**
 * Generate unique filename for chat image
 */
export function generateChatImageFilename(userId: string): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  return `${userId}/${timestamp}_${random}.webp`;
}

/**
 * Validate image size (reject if too large after compression)
 */
export function validateImageSize(sizeKB: number, maxSizeKB: number = 1024): boolean {
  if (sizeKB > maxSizeKB) {
    console.warn(`⚠️ [ChatImage] Image too large: ${sizeKB}KB (max: ${maxSizeKB}KB)`);
    return false;
  }
  return true;
}
